import { KeyboardEvent, useRef } from 'react';
import { MOOD_SCALE } from '../lib/constants';
import { useI18n } from '../i18n';

interface Props {
  value: number | null;
  onChange: (value: number) => void;
  labelledBy: string;
  describedBy?: string;
}

// ScaleTile row from the design system as an ARIA radio group: one Tab stop,
// arrow keys move and select (WAI-ARIA APG radio pattern), the chosen word is
// read back underneath, and the chosen tile shows a ✓ plus a 3:1 ring.
export function MoodPicker({ value, onChange, labelledBy, describedBy }: Props) {
  const { t } = useI18n();
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const picked = MOOD_SCALE.find((m) => m.value === value);
  const focusIndex = picked ? MOOD_SCALE.indexOf(picked) : 0;

  function onKeyDown(e: KeyboardEvent<HTMLButtonElement>, i: number) {
    const last = MOOD_SCALE.length - 1;
    let next = -1;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = i === last ? 0 : i + 1;
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = i === 0 ? last : i - 1;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = last;
    if (next < 0) return;
    e.preventDefault();
    onChange(MOOD_SCALE[next].value);
    refs.current[next]?.focus();
  }

  return (
    <div>
      <div className="sx-scale" role="radiogroup" aria-labelledby={labelledBy} aria-describedby={describedBy}>
        {MOOD_SCALE.map((m, i) => {
          const checked = value === m.value;
          const label = t[`mood.${m.value}`];
          return (
            <button
              key={m.value}
              ref={(el) => (refs.current[i] = el)}
              type="button"
              role="radio"
              aria-checked={checked}
              aria-label={`${m.value} – ${label}`}
              tabIndex={i === focusIndex ? 0 : -1}
              className={`sx-tile${checked ? ' active' : ''}`}
              onClick={() => onChange(m.value)}
              onKeyDown={(e) => onKeyDown(e, i)}
            >
              {checked && <span className="tick" aria-hidden="true">✓</span>}
              <span className="emoji" aria-hidden="true">{m.emoji}</span>
              <span className="lbl" aria-hidden="true">{m.value}</span>
            </button>
          );
        })}
      </div>
      <div className="sx-scale-ends" aria-hidden="true">
        <span>{t['mood.1']}</span>
        <span>{t['mood.5']}</span>
      </div>
      <p className="sx-scale-picked" aria-hidden="true">
        {picked ? t[`mood.${picked.value}`] : ''}
      </p>
    </div>
  );
}
